import React from "react";
import { ArrowDownRight, Check, Edit2, Plus, X } from "lucide-react";

export default function DashboardTodoSection({
  isEditMode,
  isMobile,
  longPressHandlers,
  handleWidgetClick,
  GlassCard,
  theme,
  icons,
  todos,
  newTodo,
  setNewTodo,
  handleAddTodo,
  editingId,
  editText,
  setEditText,
  saveEdit,
  cancelEdit,
  toggleTodo,
  startEdit,
  deleteTodo,
  deleteWidget,
}) {
  return (
    <div key="todo" className="h-full">
      <div
        className={`h-full ${isEditMode ? "animate-shake ring-4 ring-blue-400/50 rounded-[2.5rem] relative z-40" : ""}`}
        style={{ animationDelay: isEditMode ? `${Math.random() * -0.5}s` : "0s" }}
      >
        <GlassCard
          {...longPressHandlers}
          onClick={handleWidgetClick}
          variant={isMobile ? "solid" : "glass"}
          isMobile={isMobile}
          className={`flex flex-col h-full bg-gradient-to-br from-white to-emerald-50/30 ${isMobile ? "(!rounded-[20px] bg-white p-4" : `p-6 border-b-[8px] ${theme.border.replace("border-", "border-")} shadow-xl ${theme.shadow}/20`}`}
          noHover
        >
          <div className={`flex justify-between items-center relative z-10 shrink-0 ${isMobile ? "mb-4" : "mb-6"}`}>
            <h3 className={`font-bold ${theme.text} flex items-center gap-2 font-do-hyeon ${isMobile ? "text-base" : "text-lg"}`}>
              <img src={icons.memo} alt="memo" className={`drop-shadow-md ${isMobile ? "w-6 h-6" : "w-8 h-8"}`} /> 할 일 목록
            </h3>
            <span className={`font-bold text-white ${theme.bg} rounded-full shadow-md border border-white/20 ${isMobile ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`}>
              {todos.filter((t) => !t.completed).length}개 남음
            </span>
          </div>

          <div className={`flex gap-2 relative z-10 shrink-0 ${isMobile ? "mb-3" : "mb-5"}`}>
            <input
              value={newTodo}
              onChange={(e) => setNewTodo(e.target.value)}
              onKeyPress={handleAddTodo}
              placeholder="할 일을 입력하세요..."
              className={`flex-1 bg-white/80 backdrop-blur-md border border-white rounded-2xl focus:bg-white focus:ring-4 focus:ring-${theme.border.replace("border-", "")}/10 outline-none transition shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] placeholder-gray-400 font-bold text-gray-700 ${isMobile ? "px-3 py-2 text-xs" : "px-5 py-3.5 text-sm"}`}
            />
            <button onClick={() => handleAddTodo({ key: "Enter" })} className={`${theme.bg} text-white rounded-2xl hover:opacity-90 transition shadow-lg ${theme.shadow}/30 hover:scale-105 active:scale-95 flex items-center justify-center ${isMobile ? "w-10" : "w-12"}`}>
              <Plus size={isMobile ? 18 : 22} strokeWidth={3} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 space-y-2.5 relative z-10">
            {todos.length === 0 && <div className="text-center py-10 text-gray-400 text-sm font-bold">할 일이 없습니다.</div>}
            {todos.map((todo) => (
              <div key={todo.id} className={`group flex items-center justify-between rounded-2xl bg-white/60 backdrop-blur-sm border border-white/50 shadow-sm hover:shadow-md hover:bg-white/90 transition-all ${isMobile ? "p-2" : "p-3"}`}>
                {editingId === todo.id ? (
                  <div className="flex items-center gap-2 flex-1 w-full">
                    <input
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                      className={`flex-1 min-w-0 bg-white border ${theme.border} rounded-xl px-2 py-1.5 text-xs outline-none shadow-inner`}
                      autoFocus
                    />
                    <button onClick={saveEdit} className={`p-1.5 ${theme.text} hover:bg-green-100 rounded-xl transition`}>
                      <Check size={14} />
                    </button>
                    <button onClick={cancelEdit} className="p-1.5 text-red-500 hover:bg-red-100 rounded-xl transition">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer" onClick={() => toggleTodo(todo.id)}>
                      <div className={`rounded-lg border-2 flex items-center justify-center transition-all duration-300 shadow-sm ${isMobile ? "w-4.5 h-4.5" : "w-6 h-6"} ${todo.completed ? `${theme.bg} ${theme.border} scale-105 rotate-3` : `bg-white border-gray-200 group-hover:${theme.border}`}`}>
                        {todo.completed && <Check size={isMobile ? 12 : 16} className="text-white" strokeWidth={4} />}
                      </div>
                      <span className={`font-bold break-all whitespace-normal leading-tight transition-colors ${isMobile ? "text-xs" : "text-sm"} ${todo.completed ? "text-gray-400 line-through decoration-2 decoration-gray-300" : `text-gray-700 group-hover:${theme.text}`}`}>
                        {todo.text}
                      </span>
                    </div>
                    <div className="flex items-center opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                      <button onClick={() => startEdit(todo)} className="p-1.5 text-gray-400 hover:text-[#354F38] hover:bg-green-50 rounded-xl transition-all">
                        <Edit2 size={12} />
                      </button>
                      <button onClick={() => deleteTodo(todo.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all">
                        <X size={12} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </GlassCard>
        {isEditMode && !isMobile && (
          <>
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                deleteWidget("todo");
              }}
              className="non-draggable absolute -top-2 -right-2 bg-red-500 text-white p-1.5 rounded-full shadow-lg hover:scale-110 transition z-50 cursor-pointer"
            >
              <X size={16} />
            </button>
            <div
              className="react-resizable-handle react-resizable-handle-se pointer-events-none absolute bottom-2 right-2 w-8 h-8 bg-white/30 backdrop-blur-md border border-white/60 text-[#2C3E30] rounded-full shadow-lg flex items-center justify-center cursor-se-resize z-50 hover:scale-110 hover:bg-white/50 transition-all"
              style={{ touchAction: "none" }}
            >
              <ArrowDownRight size={18} strokeWidth={3} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
